import { useEffect, useState, useCallback } from 'react';
import { Printer as PrinterIcon, CheckCircle2, AlertTriangle, XCircle, Loader2 } from 'lucide-react';
import api from '@/lib/api';
import { isLikelyMobileCashier } from '@/lib/printBridge';
import { isNativeThermalAvailable } from '@/lib/nativeThermalPrint';

const NATIVE_CTS = ['usb', 'bluetooth', 'sunmi'];

/**
 * iter677 — Branch Print Health Badge.
 *
 * Small pill rendered at the top of the POS page that summarises whether
 * every configured printer at the current branch is reachable RIGHT NOW.
 *
 *   • Green  "All n printers ready"          — every printer responded
 *   • Amber  "k of n printers reachable"     — partial outage
 *   • Red    "No printers reachable"         — total outage
 *   • Sky    "Checking…"                     — in flight
 *
 * Tapping the pill expands an inline panel listing each printer with
 * its individual reachability state and last-check time, plus a "Re-check"
 * button. Auto-rechecks every 5 minutes while the page is foregrounded.
 *
 * Reachability check uses fetch(HEAD) + mode:'no-cors' against the
 * printer's HTTPS endpoint with a 1.5s timeout. We can't read the
 * response (CORS) but a resolved fetch means a TCP connection happened,
 * which is sufficient signal that the printer is online + cert-trusted.
 */

const CHECK_INTERVAL_MS = 5 * 60 * 1000;
const PING_TIMEOUT_MS = 1500;

async function pingPrinter(ip) {
  if (!ip) return { ok: false, err: 'no-ip' };
  try {
    const ctrl = new AbortController();
    const t = setTimeout(() => ctrl.abort(), PING_TIMEOUT_MS);
    await fetch(`https://${ip}/`, { mode: 'no-cors', signal: ctrl.signal, cache: 'no-store' });
    clearTimeout(t);
    return { ok: true };
  } catch (e) {
    return { ok: false, err: String(e?.name || e) };
  }
}

export default function PrinterHealthBadge({ branchId }) {
  const [printers, setPrinters] = useState([]);      // IP printers (browser-pingable)
  const [nativePrinters, setNativePrinters] = useState([]); // USB/BT/Sunmi (app-printed)
  const [results, setResults] = useState({}); // { printerId: {ok, ts, err} }
  const [loading, setLoading] = useState(false);
  const [expanded, setExpanded] = useState(false);
  const nativeReady = isNativeThermalAvailable();

  const runCheck = useCallback(async () => {
    setLoading(true);
    let all = [];
    try {
      const token = localStorage.getItem('cashier_token') || localStorage.getItem('token');
      const { data } = await api.get('/cashier/printers', { headers: { Authorization: `Bearer ${token}` } });
      all = (data || []).filter(p => {
        if (!branchId) return true;
        return !p.branch_id || p.branch_id === branchId;
      });
    } catch (_e) {
      setLoading(false);
      return;
    }
    const ipList = all.filter(p => p.ip_address);
    const nativeList = all.filter(p => !p.ip_address && NATIVE_CTS.includes((p.connection_type || '').toLowerCase()));
    setPrinters(ipList);
    setNativePrinters(nativeList);
    const next = {};
    await Promise.all(ipList.map(async (p) => {
      const r = await pingPrinter(p.ip_address);
      next[p.id] = { ...r, ts: Date.now() };
    }));
    setResults(next);
    setLoading(false);
  }, [branchId]);

  useEffect(() => {
    // Render for tablet/iPad cashiers OR inside the native Android app.
    if (!isLikelyMobileCashier() && !nativeReady) return;
    runCheck();
    const iv = setInterval(runCheck, CHECK_INTERVAL_MS);
    const onFocus = () => runCheck();
    window.addEventListener('focus', onFocus);
    return () => {
      clearInterval(iv);
      window.removeEventListener('focus', onFocus);
    };
  }, [runCheck, nativeReady]);

  if (!isLikelyMobileCashier() && !nativeReady) return null;
  const totalConfigured = printers.length + nativePrinters.length;
  if (!totalConfigured && !loading) return null;

  // Native USB/BT/Sunmi printers count as "ready" when the native plugin is
  // present (they can't be IP-pinged); IP printers use the reachability ping.
  const nativeOk = nativeReady ? nativePrinters.length : 0;
  const ipOk = printers.filter(p => results[p.id]?.ok).length;
  const total = totalConfigured;
  const ok = ipOk + nativeOk;
  const tone = loading ? 'sky' : ok === total ? 'emerald' : ok === 0 ? 'rose' : 'amber';
  const Icon = loading ? Loader2 : ok === total ? CheckCircle2 : ok === 0 ? XCircle : AlertTriangle;
  const label = loading
    ? 'Checking printers…'
    : total === 0
      ? 'No printers configured'
      : ok === total
        ? `All ${total} printer${total === 1 ? '' : 's'} ready`
        : ok === 0
          ? 'No printers reachable'
          : `${ok} of ${total} printers ready`;

  const toneCls = {
    emerald: 'bg-emerald-100 text-emerald-900 border-emerald-300 hover:bg-emerald-200',
    amber:   'bg-amber-100 text-amber-900 border-amber-300 hover:bg-amber-200',
    rose:    'bg-rose-100 text-rose-900 border-rose-300 hover:bg-rose-200',
    sky:     'bg-sky-100 text-sky-900 border-sky-300',
  }[tone];

  return (
    <div className="relative inline-block" data-testid="printer-health-badge">
      <button
        type="button"
        onClick={() => setExpanded(!expanded)}
        className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full border text-[11px] font-semibold ${toneCls} transition-colors`}
        title="Tap to see per-printer status"
        data-testid="printer-health-pill"
      >
        <Icon size={12} className={loading ? 'animate-spin' : ''} />
        <span>{label}</span>
      </button>
      {expanded && (
        <div className="absolute z-50 top-full mt-1.5 right-0 w-72 bg-white rounded-lg border-2 border-zinc-200 shadow-lg p-2.5 space-y-1.5" data-testid="printer-health-panel">
          <div className="flex items-center justify-between border-b pb-1.5">
            <p className="text-[11px] font-bold">Printer status</p>
            <button
              type="button"
              onClick={() => runCheck()}
              disabled={loading}
              className="text-[10px] text-sky-700 hover:text-sky-900 underline underline-offset-2 disabled:opacity-50"
              data-testid="printer-health-recheck"
            >
              {loading ? 'Checking…' : 'Re-check'}
            </button>
          </div>
          {printers.map(p => {
            const r = results[p.id];
            return (
              <div key={p.id} className="flex items-center gap-2 text-[11px]" data-testid={`printer-health-row-${p.id}`}>
                <span className={`h-2 w-2 rounded-full ${r?.ok ? 'bg-emerald-500' : r ? 'bg-rose-500' : 'bg-zinc-300'}`} />
                <PrinterIcon size={11} className="text-zinc-500" />
                <span className="flex-1 truncate">{p.name}</span>
                <span className="text-[10px] text-zinc-500 font-mono">{p.ip_address}</span>
              </div>
            );
          })}
          {nativePrinters.map(p => (
            <div key={p.id} className="flex items-center gap-2 text-[11px]" data-testid={`printer-health-native-row-${p.id}`}>
              <span className={`h-2 w-2 rounded-full ${nativeReady ? 'bg-emerald-500' : 'bg-amber-500'}`} />
              <PrinterIcon size={11} className="text-zinc-500" />
              <span className="flex-1 truncate">{p.name}</span>
              <span className="text-[10px] text-zinc-500 uppercase">{nativeReady ? (p.connection_type || 'native') : 'needs app'}</span>
            </div>
          ))}
          {printers.length > 0 && ipOk < printers.length && (
            <div className="text-[10px] text-rose-700 pt-1 border-t">
              💡 Unreachable printer? Check: same Wi-Fi, printer powered ON, cert accepted (Settings → Printers → Accept ALL).
            </div>
          )}
        </div>
      )}
    </div>
  );
}
